'use client';
import { Container } from '@/components/primitives/Container/Container';
import {
  ComposedChart,
  XAxis,
  YAxis,
  ReferenceArea,
  ResponsiveContainer,
  Label,
  Line,
} from 'recharts';
import {
  parseSegments,
  MAX_BAR_AREA_PX,
} from '@/components/authorable/shared/PricingChart/PricingBarChart.styles';
import { barChartVariants, barChartConfig, formatHourLabel } from './PricingBarChart.styles';
import { IPricingChartModularBlock } from '@/.generated';
import { WithMetadata } from '@/lib/types';
import { getCSLPAttributes } from '@/utils/type-guards';
import { useElementWidth } from '@/lib/hooks/useElementWidth';

const renderBarLabel = (label: string) => {
  const BarLabel = (props: Record<string, unknown>) => {
    const viewBox = props.viewBox as
      | { x?: number; y?: number; width?: number; height?: number }
      | undefined;
    const { x = 0, y = 0, width = 0, height = 0 } = viewBox ?? {};
    const minWidth = label.length * barChartConfig.typography.barLabel.fontSize * 0.45 + 8;
    if (width < minWidth) return null;
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={barChartConfig.colors.barLabel}
        className="text-xs xm:text-[14.4px] font-bold"
      >
        {label}
      </text>
    );
  };
  return BarLabel;
};

export const PricingBarChart = (props: WithMetadata<IPricingChartModularBlock>) => {
  const { base, title } = barChartVariants();
  const segments = parseSegments(props.items);
  // Measured plot width drives the label-overlap calculation below
  const [chartRef, chartWidth] = useElementWidth<HTMLDivElement>();

  if (!segments.length) return null;

  // Only label both endpoints "12 a.m." when a single bar spans the whole day (12 a.m. → 12 a.m.)
  const isSingleFullDayBar =
    segments.length === 1 &&
    segments[0].startHour === 0 &&
    segments[0].endHour === barChartConfig.totalHours;

  const yMax = 4;
  const yTicks = [0, 1, 2, 3, 4];

  // Converts a CMS top_margin (px) into a chart y-value within the 80px bar area
  const yValueFromTopMargin = (topMargin: number): number => {
    const heightPx = Math.max(0, MAX_BAR_AREA_PX - topMargin);
    return (heightPx / MAX_BAR_AREA_PX) * yMax;
  };

  const boundaryHours = Array.from(
    new Set(segments.flatMap((s) => [s.startHour, s.endHour]))
  ).filter((h) => h > 0 && h < barChartConfig.totalHours);

  const majorHourSet = new Set(boundaryHours);
  const allHourTicks = Array.from({ length: barChartConfig.totalHours + 1 }, (_, i) => i);
  const anchorData = [
    { x: 0, y: 0 },
    { x: barChartConfig.totalHours, y: yMax },
  ];

  // Pixels per hour from the measured plot width (0 until the chart has mounted)
  const pixelsPerHour = chartWidth > 0 ? chartWidth / barChartConfig.totalHours : 0;

  // Estimated rendered width (px) of a single-line hour label, e.g. "2 a.m."
  const estimateLabelWidth = (hour: number): number =>
    formatHourLabel(hour).length *
    barChartConfig.typography.majorHourLabel.fontSize *
    barChartConfig.labelOverlap.charWidthRatio;

  // Hours that actually render a text label (noon shows "PM"; endpoints handled separately)
  const labeledHours = boundaryHours.filter((h) => h !== 12).sort((a, b) => a - b);

  // Wrap a label to two lines only when its single line would collide with a neighbour
  const wrapHourSet = new Set<number>();
  if (pixelsPerHour > 0) {
    labeledHours.forEach((hour, i) => {
      const neighbours = [labeledHours[i - 1], labeledHours[i + 1]];
      const halfWidth = estimateLabelWidth(hour) / 2;
      const collides = neighbours.some((neighbour) => {
        if (neighbour === undefined) return false;
        const gapPx = Math.abs(hour - neighbour) * pixelsPerHour;
        const needed =
          halfWidth + estimateLabelWidth(neighbour) / 2 + barChartConfig.labelOverlap.paddingPx;
        return needed > gapPx;
      });
      if (collides) wrapHourSet.add(hour);
    });
  }

  // Reserve the taller axis only when at least one label wraps
  const hasWrappedLabel = wrapHourSet.size > 0;

  // "AM" is centred on the second tick (1 a.m.) — the midpoint of the first and third ticks
  const amHour = 1;

  const renderXAxisTick = (tickProps: {
    x?: string | number;
    y?: string | number;
    payload?: { value: number };
  }) => {
    const x = Number(tickProps.x ?? 0);
    const y = Number(tickProps.y ?? 0);
    const hour = Number(tickProps.payload?.value ?? 0);
    const isStart = hour === 0;
    const isAm = hour === amHour;
    const isPm = hour === 12;
    const isMajor = majorHourSet.has(hour);
    // Chart endpoints (hour 0 and hour 24, both 12 a.m.) are excluded from boundaryHours
    const isEnd = hour === barChartConfig.totalHours;

    // Builds the hour label below the axis, splitting into two lines on narrow screens
    const renderHourLabel = (anchor: 'start' | 'middle' | 'end') => {
      const label = formatHourLabel(hour);
      const [hourPart, periodPart] = label.split(' ');
      return (
        <text
          x={x}
          y={y + barChartConfig.spacing.majorTickLabelGap}
          textAnchor={anchor}
          dominantBaseline="hanging"
          fill={barChartConfig.colors.tickText}
          fontSize={barChartConfig.typography.majorHourLabel.fontSize}
          fontWeight={barChartConfig.typography.majorHourLabel.fontWeight}
        >
          {wrapHourSet.has(hour) ? (
            <>
              <tspan x={x} dy="0">
                {hourPart}
              </tspan>
              <tspan x={x} dy="14">
                {periodPart}
              </tspan>
            </>
          ) : (
            label
          )}
        </text>
      );
    };

    // Edge ticks sit on the plot boundary where half the stroke is clipped; nudge them inward to render full width
    const edgeAdjustedX = (stroke: number): number => {
      if (isStart) return x + stroke / 2;
      if (isEnd) return x - stroke / 2;
      return x;
    };

    // A full-height tick line at the current hour
    const majorTickX = edgeAdjustedX(barChartConfig.ticks.majorStroke);
    const majorTickLine = (
      <line
        x1={majorTickX}
        y1={y}
        x2={majorTickX}
        y2={y + barChartConfig.ticks.majorHeight}
        stroke={barChartConfig.ticks.majorColor}
        strokeWidth={barChartConfig.ticks.majorStroke}
      />
    );

    // The thin tick line shared by the first tick and the unlabelled interior ticks
    const minorTickX = edgeAdjustedX(barChartConfig.ticks.minorStroke);
    const minorTickLine = (
      <line
        x1={minorTickX}
        y1={y}
        x2={minorTickX}
        y2={y + barChartConfig.ticks.majorHeight}
        stroke={barChartConfig.ticks.minorColor}
        strokeWidth={barChartConfig.ticks.minorStroke}
      />
    );

    // AM/PM region marker on the tick row
    const renderAmPm = (text: 'AM' | 'PM', anchor: 'start' | 'middle') => (
      <text
        x={x}
        y={y + barChartConfig.spacing.tickLabelOffsetY}
        textAnchor={anchor}
        dominantBaseline="central"
        fill={barChartConfig.colors.tickText}
        fontSize={barChartConfig.typography.amPmLabel.fontSize}
        fontWeight={barChartConfig.typography.amPmLabel.fontWeight}
        style={{ lineHeight: barChartConfig.typography.amPmLabel.lineHeight }}
      >
        {text}
      </text>
    );

    // Noon marker sits on the tick row
    if (isPm) {
      return renderAmPm('PM', barChartConfig.spacing.amPmTextAnchor.middle);
    }

    // Chart start: keep the first tick visible; a single full-day bar labels it "12 a.m."
    if (isStart) {
      if (isSingleFullDayBar) {
        return (
          <g>
            {majorTickLine}
            {renderHourLabel(barChartConfig.spacing.amPmTextAnchor.start)}
          </g>
        );
      }
      return minorTickLine;
    }

    // Second tick (1 a.m.) is replaced by the centred "AM" period marker
    if (isAm) {
      return renderAmPm('AM', barChartConfig.spacing.amPmTextAnchor.middle);
    }

    // Interior boundaries always label; the chart end only labels for a single full-day bar
    if (isMajor || (isEnd && isSingleFullDayBar)) {
      const labelAnchor = isEnd ? 'end' : barChartConfig.spacing.amPmTextAnchor.middle;
      return (
        <g>
          {majorTickLine}
          {renderHourLabel(labelAnchor)}
        </g>
      );
    }

    return minorTickLine;
  };

  return (
    <Container
      componentName="PricingBarChart"
      metaId={props._metadata?.uid}
      containerBleed={props.use_wide_layout}
    >
      <div className={base()}>
        {props.title && (
          <div className={title()} {...getCSLPAttributes(props.$?.title)}>
            {props.title}
          </div>
        )}
        {/* aria-hidden + inert prevents the SVG from receiving focus on click (avoids black outline on Windows) */}
        <div aria-hidden="true" inert ref={chartRef}>
          <ResponsiveContainer
            width="100%"
            height={
              hasWrappedLabel
                ? barChartConfig.dimensions.containerHeightMobile
                : barChartConfig.dimensions.containerHeight
            }
          >
            <ComposedChart
              data={anchorData}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              accessibilityLayer={false}
            >
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, barChartConfig.totalHours]}
                ticks={allHourTicks}
                interval={0}
                tick={renderXAxisTick}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                height={
                  hasWrappedLabel
                    ? barChartConfig.dimensions.xAxisHeightMobile
                    : barChartConfig.dimensions.xAxisHeight
                }
              />
              <YAxis type="number" domain={[0, yMax]} ticks={yTicks} interval={1} hide />
              <Line
                dataKey={barChartConfig.line.dataKey}
                stroke={barChartConfig.line.stroke}
                dot={barChartConfig.line.dot}
                activeDot={barChartConfig.line.activeDot}
                legendType={barChartConfig.line.legendType}
              />
              {segments.map((seg, i) => (
                <ReferenceArea
                  key={i}
                  x1={seg.startHour}
                  x2={seg.endHour}
                  y1={0}
                  y2={yValueFromTopMargin(seg.topMargin)}
                  fill={seg.fill}
                  fillOpacity={1}
                  stroke="none"
                >
                  {seg.label && <Label content={renderBarLabel(seg.label)} />}
                </ReferenceArea>
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Container>
  );
};
